/* ========================================
   CategoryTabs - AliExpress Style Horizontal Tabs
   - Sticky scrollable tabs below header
   - Filter products by category
   - Supports RTL (Arabic) and LTR (English)
   - Scroll hint arrows when more tabs are off-screen
======================================== */

import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import './CategoryTabs.css';

// Default tab order (highest demand + monetization first)
const TAB_ORDER = [
  'all',
  'recommended',
  'vehicles',
  'spare-parts',
  'electronics',
  'mobile_accessories',
  'laptops_accessories',
  'services',
  // 'jobs' — removed from user-facing category tabs (2026-02-28)
  'clothes_shoes',
  'sports_equipment',
  'health_fitness',
  'games_toys',
  'commodities',
  'construction',
  'building-materials',
  'agriculture',
  'food-beverages',
  'energy-solar',
  'medical-devices',
  'medical-supplies',
  'womens-beauty',
  'mens-fashion',
  'apparel',
  'furniture',
  'home-supplies',
  'office-school',
  'sports-fitness',
  'kids-toys',
  'gifts-crafts',
  'jewellery',
  'livestock',
  'industrial-machinery',
  'equipment-tools',
  'electronic-equipment',
  'manufacturing',
  'chemicals',
  'metals-alloys',
  'fabric-materials',
  'environmental',
  'garden-outdoor',
  'it-software'
];

export function CategoryTabs({ categories, activeId, onChange, showRecommended = true }) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const tabsRef = useRef(null);
  const activeTabRef = useRef(null);
  
  // Scroll hint state
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  
  // Scroll amount for arrow click
  const scrollByAmount = useMemo(() => 200, []);

  // Create ordered categories list
  const orderedCategories = React.useMemo(() => {
    // Add "All" and "Recommended" tabs
    const specialTabs = [
      { key: 'all', name_en: 'All', name_ar: 'الكل', icon: null },
    ];
    
    if (showRecommended) {
      specialTabs.push({ key: 'recommended', name_en: 'Recommended', name_ar: 'مقترح', icon: 'sparkles' });
    }
    
    // Filter out special items and student_hub from categories
    const regularCategories = categories.filter(c => 
      c.key !== 'all' && 
      c.key !== 'recommended' && 
      c.key !== 'student_hub' &&
      !c.special
    );
    
    // Sort categories by the defined order
    const sortedCategories = [...regularCategories].sort((a, b) => {
      const indexA = TAB_ORDER.indexOf(a.key);
      const indexB = TAB_ORDER.indexOf(b.key);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    
    return [...specialTabs, ...sortedCategories];
  }, [categories, showRecommended]);

  // Update scroll state to show/hide arrows
  const updateScrollState = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;

    const max = el.scrollWidth - el.clientWidth;
    
    // Normalize scroll position (handle RTL browsers with negative scrollLeft)
    let pos = el.scrollLeft;
    if (isRTL && pos < 0) {
      pos = Math.abs(pos);
    }
    pos = Math.min(Math.max(pos, 0), max);

    // Determine if we can scroll in each direction
    // Using threshold of 4px to account for rounding
    setCanScrollPrev(pos > 4);
    setCanScrollNext(max - pos > 4);
  }, [isRTL]);

  // Scroll to previous (left in LTR, right in RTL)
  const scrollPrev = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: (isRTL ? 1 : -1) * scrollByAmount, behavior: 'smooth' });
  }, [isRTL, scrollByAmount]);

  // Scroll to next (right in LTR, left in RTL)
  const scrollNext = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: (isRTL ? -1 : 1) * scrollByAmount, behavior: 'smooth' });
  }, [isRTL, scrollByAmount]);

  // Set up scroll listeners
  useEffect(() => {
    updateScrollState();
    const el = tabsRef.current;
    if (!el) return;

    const onScroll = () => updateScrollState();
    const onResize = () => updateScrollState();

    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    // Update after fonts/images settle
    const t = setTimeout(updateScrollState, 250);

    return () => {
      clearTimeout(t);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [isRTL, updateScrollState, orderedCategories]);

  // Scroll active tab into view
  useEffect(() => {
    if (activeTabRef.current && tabsRef.current) {
      const container = tabsRef.current;
      const activeTab = activeTabRef.current;
      
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      
      // Center the active tab in the container
      const scrollLeft = activeTab.offsetLeft - (containerRect.width / 2) + (tabRect.width / 2);
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  }, [activeId]);

  return (
    <div className="category-tabs-wrapper" data-testid="category-tabs">
      <div className="category-tabs" ref={tabsRef}>
        {orderedCategories.map((category) => (
          <button
            key={category.key}
            ref={activeId === category.key ? activeTabRef : null}
            className={`category-tab ${activeId === category.key ? 'active' : ''}`}
            onClick={() => onChange(category.key)}
            data-testid={`tab-${category.key}`}
          >
            {category.key === 'recommended' && (
              <Sparkles size={14} className="tab-icon" />
            )}
            <span className="tab-label">
              {isRTL ? category.name_ar : category.name_en}
            </span>
          </button>
        ))}
      </div>
      
      {/* Scroll hint arrow - PREV (shows after scrolling) */}
      {canScrollPrev && (
        <div className={`scroll-hint-arrow scroll-hint-prev ${isRTL ? 'rtl' : 'ltr'}`}>
          <div className="scroll-hint-gradient" />
          <button
            type="button"
            onClick={scrollPrev}
            className="scroll-hint-btn"
            aria-label="Scroll categories back"
            data-testid="category-scroll-prev"
          >
            {isRTL ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
      )}
      
      {/* Scroll hint arrow - NEXT (shows when more tabs off-screen) */}
      {canScrollNext && (
        <div className={`scroll-hint-arrow scroll-hint-next ${isRTL ? 'rtl' : 'ltr'}`}>
          <div className="scroll-hint-gradient" />
          <button
            type="button"
            onClick={scrollNext}
            className="scroll-hint-btn"
            aria-label="Scroll categories"
            data-testid="category-scroll-next"
          >
            {isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>
      )}
    </div>
  );
}

export default CategoryTabs;
